import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicClientQualificationApi } from '../api';
import {
  Loader2,
  FileText,
  Download,
  Trash2,
  CheckCircle2,
  Clock,
  Send,
  AlertCircle,
  Shield,
  UserCheck,
  Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';
import { QualificationFormFields, buildFormData } from './ClientQualification';

const STATUS_CONFIG = {
  draft: { label: 'Aguardando preenchimento', color: 'bg-gray-500/20 text-gray-400' },
  pending_analysis: { label: 'Enviado · aguardando análise', color: 'bg-yellow-500/20 text-yellow-400' },
  in_analysis: { label: 'Em análise', color: 'bg-blue-500/20 text-blue-400' },
  approved: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  correction_needed: { label: 'Necessita correção', color: 'bg-red-500/20 text-red-400' },
};

const STEPS = [
  { key: 'fill', label: 'Preenchimento' },
  { key: 'submitted', label: 'Enviado' },
  { key: 'analysis', label: 'Em análise' },
  { key: 'result', label: 'Resultado' },
];

const currentStep = (status) => {
  switch (status) {
    case 'draft': return 0;
    case 'pending_analysis': return 1;
    case 'in_analysis': return 2;
    case 'correction_needed': return 3;
    case 'approved': return 3;
    default: return 0;
  }
};

const ClassificacaoCliente = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await publicClientQualificationApi.get(token);
        setData(res.data);
        setFormData(buildFormData(res.data));
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const statusCfg = STATUS_CONFIG[data?.status] || STATUS_CONFIG.draft;

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await publicClientQualificationApi.uploadAttachment(token, file);
      toast.success('Documento anexado com sucesso');
      const res = await publicClientQualificationApi.get(token);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anexar documento');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (attId) => {
    if (!window.confirm('Remover este documento?')) return;
    setRemovingId(attId);
    try {
      await publicClientQualificationApi.deleteAttachment(token, attId);
      toast.success('Documento removido');
      const res = await publicClientQualificationApi.get(token);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover documento');
    } finally {
      setRemovingId(null);
    }
  };

  const handleDownload = async (att) => {
    setDownloadingId(att.id);
    try {
      const res = await publicClientQualificationApi.downloadAttachment(token, att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', att.file_name || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao baixar documento');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = {};
    if (!formData.fantasy_name.trim()) missing.fantasy_name = true;
    if (!formData.company_name.trim()) missing.company_name = true;
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      toast.error('Informe o Nome Fantasia e a Razão Social (campos obrigatórios)');
      const first = missing.fantasy_name ? 'fq-fantasy_name' : 'fq-company_name';
      const el = document.getElementById(first);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const payload = { ...formData };
      delete payload.quality_evaluation;
      delete payload.quality_evaluation_reason;
      delete payload.quality_evaluation_date;
      delete payload.quality_responsible;
      delete payload.quality_responsible_date;
      if (!payload.has_hazardous_products) delete payload.has_hazardous_products;
      if (!payload.hazmat_fds_sent) delete payload.hazmat_fds_sent;
      if (!payload.hazmat_ghs_label) delete payload.hazmat_ghs_label;
      if (!payload.hazmat_emergency_envelope) delete payload.hazmat_emergency_envelope;
      if (!payload.transport_antt_compliant) delete payload.transport_antt_compliant;
      await publicClientQualificationApi.submit(token, payload);
      setSubmitted(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao enviar a classificação'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B1220' }} className="flex items-center justify-center">
        <Loader2 className="animate-spin text-green-500" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B1220' }} className="flex items-center justify-center p-6">
        <div className="card p-8 max-w-md w-full text-center">
          <Shield size={40} className="mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Link inválido ou expirado</h1>
          <p className="text-gray-400 text-sm">Se você recebeu este link por e-mail, verifique se copiou o endereço completo.</p>
        </div>
      </div>
    );
  }

  const stepIndex = currentStep(data.status);

  return (
    <div style={{ minHeight: '100vh', background: '#0B1220' }}>
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}
          >
            <UserCheck size={20} style={{ color: '#fff' }} />
          </div>
          <div>
            <p className="text-white font-semibold">QualyX+ SGQ</p>
            <p className="text-gray-400 text-xs">Classificação de Cliente</p>
          </div>
        </div>

        {/* Header */}
        <div className="card p-6">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="font-mono text-green-400">{data.code}</span>
            <span className={`px-2 py-1 rounded-full text-xs ${statusCfg.color}`}>{statusCfg.label}</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Formulário de Classificação de Cliente</h1>
          <p className="text-gray-400 text-sm">
            Preencha os dados da empresa, os documentos e anexe a documentação solicitada.
          </p>
        </div>

        {/* Progress indicator */}
        <div className="card p-5">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center gap-1.5 flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      idx < stepIndex ? 'bg-green-500/20 text-green-400' :
                      idx === stepIndex ? 'bg-green-500 text-white' :
                      'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {idx < stepIndex ? <CheckCircle2 size={16} /> : idx + 1}
                  </div>
                  <span className={`text-xs ${idx <= stepIndex ? 'text-gray-200' : 'text-gray-500'}`}>
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded ${idx < stepIndex ? 'bg-green-500' : 'bg-gray-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          {data.status === 'correction_needed' && (
            <p className="text-xs text-green-400 text-center mt-3">
              A classificação retornou para correção — faça os ajustes e envie novamente.
            </p>
          )}
          {data.status === 'approved' && (
            <p className="text-xs text-green-400 text-center mt-3">
              Classificação aprovada. Obrigado pelo preenchimento.
            </p>
          )}
        </div>

        {/* Sucesso */}
        {submitted && (
          <div className="p-6 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
            <CheckCircle2 size={40} className="mx-auto text-green-400 mb-2" />
            <h2 className="text-lg font-bold text-white">Classificação enviada com sucesso!</h2>
            <p className="text-gray-400 text-sm mt-1">
              Sua solicitação foi registrada e será analisada pela nossa equipe. Você poderá acompanhar correções
              acessando o mesmo link deste e-mail.
            </p>
          </div>
        )}

        {/* Correção pendente */}
        {data.status === 'correction_needed' && data.admin_observation && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-red-400 font-medium flex items-center gap-2 mb-1">
              <AlertCircle size={16} /> Correção solicitada pela nossa equipe
            </p>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{data.admin_observation}</p>
          </div>
        )}

        {/* Formulário */}
        {data.status !== 'approved' && !submitted && (
          <form onSubmit={handleSubmit} className="card p-6 space-y-6">
            <QualificationFormFields
              formData={formData}
              setFormData={setFormData}
              errors={fieldErrors}
              onClearError={(key) => setFieldErrors((f) => ({ ...f, [key]: false }))}
            />

            {/* Documentos anexados */}
            <div>
              <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide bg-gray-800/60 px-3 py-2 rounded mb-3 flex items-center gap-2">
                <Paperclip size={16} /> Documentos / Anexos
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Anexe cópias simples dos documentos informados no checklist acima.
              </p>

              {(data.attachments || []).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  {data.attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between gap-2 border border-gray-700 rounded-lg px-3 py-2 bg-gray-800/40">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={16} className="text-green-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-gray-200 truncate">{att.file_name}</p>
                          <p className="text-xs text-gray-500">
                            {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleDownload(att)}
                          className="p-1.5 text-blue-400 hover:text-blue-300"
                          title="Baixar"
                        >
                          {downloadingId === att.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(att.id)}
                          className="p-1.5 text-red-400 hover:text-red-300"
                          title="Remover"
                        >
                          {removingId === att.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <label
                className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-gray-600 rounded-lg p-4 text-sm text-gray-400 hover:text-green-400 hover:border-green-500/50 cursor-pointer transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {uploading ? <Loader2 className="animate-spin text-green-500" size={18} /> : <Paperclip size={18} />}
                {uploading ? 'Enviando documento...' : 'Clique para anexar um documento'}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) handleUpload(f);
                  }}
                />
              </label>
            </div>

            {/* Enviar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-gray-700">
              <p className="text-sm text-gray-500 flex-1">
                <Clock size={14} className="inline mr-1" />
                Ao enviar, a solicitação será analisada pela nossa equipe.
              </p>
              <button type="submit" className="btn-primary flex items-center gap-2 justify-center" disabled={submitting}>
                {submitting ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                Enviar classificação
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClassificacaoCliente;